import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './constants';
import type { Locale } from './types';

const LOCALE_LIKE_SEGMENT_PATTERN = /^[a-z]{2}(?:-[A-Z]{2})?$/;
const LOCALE_ALIASES: Record<string, Locale> = {
  'zh-HK': 'zh-CN',
};

function normalizePathname(pathname: string): string {
  if (!pathname) {
    return '/';
  }

  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function isSupportedLocale(value: string | null | undefined): value is Locale {
  return value !== null && value !== undefined && SUPPORTED_LOCALES.includes(value as Locale);
}

export function getLocaleFromPathname(pathname: string): Locale | null {
  const normalizedPathname = normalizePathname(pathname);
  const [firstSegment] = normalizedPathname.split('/').filter(Boolean);

  return isSupportedLocale(firstSegment) ? firstSegment : null;
}

export function stripLocaleFromPathname(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname);
  const segments = normalizedPathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return '/';
  }

  if (!isSupportedLocale(segments[0])) {
    return normalizedPathname;
  }

  const nextPath = `/${segments.slice(1).join('/')}`;
  return nextPath === '/' ? '/' : nextPath.replace(/\/+$/, '') || '/';
}

export function stripPotentialLocaleFromPathname(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname);
  const segments = normalizedPathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return '/';
  }

  if (!LOCALE_LIKE_SEGMENT_PATTERN.test(segments[0])) {
    return normalizedPathname;
  }

  const nextPath = `/${segments.slice(1).join('/')}`;
  return nextPath === '/' ? '/' : nextPath.replace(/\/+$/, '') || '/';
}

export function getLocalizedPath(path: string, locale: Locale): string {
  if (!path.startsWith('/')) {
    return path;
  }

  const url = new URL(path, 'https://tutorfounder.local');
  const localizedPathname = stripLocaleFromPathname(url.pathname);
  const basePath = localizedPathname === '/' ? '' : localizedPathname;

  return `/${locale}${basePath}${url.search}${url.hash}`;
}

export function getLocaleRedirectPath(pathname: string, search = '', hash = ''): string {
  const normalizedPathname = normalizePathname(pathname);
  const [firstSegment] = normalizedPathname.split('/').filter(Boolean);
  const aliasedLocale = firstSegment ? LOCALE_ALIASES[firstSegment] : null;

  if (aliasedLocale) {
    const nextPathname = stripPotentialLocaleFromPathname(pathname);
    return getLocalizedPath(`${nextPathname}${search}${hash}`, aliasedLocale);
  }

  const nextPathname = stripPotentialLocaleFromPathname(pathname);
  return getLocalizedPath(`${nextPathname}${search}${hash}`, DEFAULT_LOCALE);
}
